import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Image as ImageIcon, 
  CheckCircle2, 
  AlertTriangle, 
  HelpCircle, 
  RefreshCw, 
  Save, 
  Search, 
  Eye 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useCruiseMetadata } from '../context/CruiseMetadataContext';

interface ProbedShip {
  name: string;
  mappedUrl: string | null;
  status: 'valid' | 'broken' | 'unmapped';
  suggestedUrl: string | null;
}

const AdminPhotoManager = () => {
  const { cruiseLines, refreshCruiseMetadata } = useCruiseMetadata();
  const [selectedCruiseLine, setSelectedCruiseLine] = useState('');
  const [filter, setFilter] = useState<'all' | 'unmapped' | 'broken' | 'valid'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Probed ship data for active cruise line
  const [shipsData, setShipsData] = useState<ProbedShip[]>([]);
  const [probing, setProbing] = useState(false);
  
  // Manual edit states
  const [editingShip, setEditingShip] = useState<string | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [saving, setSaving] = useState(false);

  // Default image config states
  const [editingDefault, setEditingDefault] = useState(false);
  const [defaultUrlInput, setDefaultUrlInput] = useState('');
  const [defaultProbedStatus, setDefaultProbedStatus] = useState<'valid' | 'broken' | 'unmapped'>('unmapped');
  const [defaultSuggestion, setDefaultSuggestion] = useState<string | null>(null);

  const selectedClData = cruiseLines.find(cl => cl.name === selectedCruiseLine);
  
  // Get filename prefixes based on cruise lines
  const getPrefix = (cruiseLine: string) => {
    const cl = cruiseLine.toLowerCase();
    if (cl.includes('royal caribbean')) return 'royal';
    if (cl.includes('carnival')) return 'carnival';
    if (cl.includes('celebrity')) return 'celebrity';
    if (cl.includes('norwegian') || cl.includes('ncl')) return 'norwegian';
    if (cl.includes('msc')) return 'msc';
    if (cl.includes('disney')) return 'disney';
    if (cl.includes('princess')) return 'princess';
    if (cl.includes('holland')) return 'holland';
    if (cl.includes('aida')) return 'aida';
    if (cl.includes('virgin')) return 'virgin';
    return cl.split(' ')[0].replace(/[^a-z0-9]/g, '');
  };

  // Generate suggested image paths
  const getSuggestions = (cruiseLine: string, shipName: string) => {
    const prefix = getPrefix(cruiseLine);
    const cleanShip = shipName.toLowerCase().trim();
    
    // Pattern 1: prefix_firstword.jpg (e.g. royal_wonder.jpg)
    const firstWord = cleanShip.split(' ')[0].replace(/[^a-z0-9]/g, '');
    
    // Pattern 2: prefix_allpacked.jpg (e.g. royal_wonderoftheseas.jpg)
    const allPacked = cleanShip.replace(/[^a-z0-9]/g, '');
    
    // Pattern 3: prefix_underscores.jpg (e.g. royal_wonder_of_the_seas.jpg)
    const underscores = cleanShip.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');

    return [
      `/images/${prefix}_${allPacked}.jpg`,
      `/images/${prefix}_${firstWord}.jpg`,
      `/images/${prefix}_${underscores}.jpg`,
    ];
  };

  // Client-side image existence check
  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = url;
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
    });
  };

  const probeShips = async () => {
    if (!selectedClData) return;
    setProbing(true);
    
    const ships = [...(selectedClData.ships || []), ...(selectedClData.retiredShips || [])].sort();
    const shipImages = selectedClData.shipImages || {};
    
    const results: ProbedShip[] = [];
    
    for (const ship of ships) {
      // Find key case-insensitively
      const mappedKey = Object.keys(shipImages).find(
        key => key.toLowerCase() === ship.toLowerCase()
      );
      const mapped = mappedKey ? shipImages[mappedKey] : null;
      let status: 'valid' | 'broken' | 'unmapped' = 'unmapped';
      let suggestedUrl: string | null = null;
      
      if (mapped) {
        const exists = await checkImageExists(mapped);
        status = exists ? 'valid' : 'broken';
      } else {
        // Find a suggestion that actually resolves
        const suggestions = getSuggestions(selectedCruiseLine, ship);
        for (const sug of suggestions) {
          const exists = await checkImageExists(sug);
          if (exists) {
            suggestedUrl = sug;
            break;
          }
        }
      }
      
      results.push({
        name: ship,
        mappedUrl: mapped,
        status,
        suggestedUrl
      });
    }
    
    // Probe Default Cruise Line Photo
    const defaultUrl = shipImages["Default"] || null;
    let defaultStatus: 'valid' | 'broken' | 'unmapped' = 'unmapped';
    let defaultSug: string | null = null;

    if (defaultUrl) {
      const exists = await checkImageExists(defaultUrl);
      defaultStatus = exists ? 'valid' : 'broken';
    } else {
      const prefix = getPrefix(selectedCruiseLine);
      const suggestions = [
        `/images/${prefix}_default.jpg`,
        `/images/${prefix}_logo.jpg`,
        `/images/${prefix}_default.png`,
        `/images/${prefix}_logo.png`
      ];
      for (const sug of suggestions) {
        const exists = await checkImageExists(sug);
        if (exists) {
          defaultSug = sug;
          break;
        }
      }
    }
    setDefaultProbedStatus(defaultStatus);
    setDefaultSuggestion(defaultSug);
    
    setShipsData(results);
    setProbing(false);
  };

  useEffect(() => {
    if (selectedCruiseLine) {
      probeShips();
    } else {
      setShipsData([]);
    }
    setEditingShip(null);
  }, [selectedCruiseLine, cruiseLines]);

  const handleQuickMap = async (shipName: string, url: string) => {
    if (!selectedClData || !selectedClData.id) return;
    
    setSaving(true);
    try {
      const updatedImages = {
        ...(selectedClData.shipImages || {})
      };
      
      // Clean up case variants of this ship name
      Object.keys(updatedImages).forEach(key => {
        if (key.toLowerCase() === shipName.toLowerCase()) {
          delete updatedImages[key];
        }
      });
      
      // Set to official casing
      updatedImages[shipName] = url;
      
      const docRef = doc(db, 'cruiseMetadata', selectedClData.id);
      await updateDoc(docRef, { shipImages: updatedImages });
      toast.success(`Mapped "${shipName}" to ${url}`);
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Error mapping photo:', error);
      toast.error('Failed to save mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (ship: ProbedShip) => {
    setEditingShip(ship.name);
    setEditUrl(ship.mappedUrl || '');
  };

  const handleSaveEdit = async (shipName: string) => {
    if (!selectedClData || !selectedClData.id) return;
    
    setSaving(true);
    try {
      const updatedImages = {
        ...(selectedClData.shipImages || {})
      };
      
      // Clean up case variants of this ship name
      Object.keys(updatedImages).forEach(key => {
        if (key.toLowerCase() === shipName.toLowerCase()) {
          delete updatedImages[key];
        }
      });
      
      if (editUrl.trim()) {
        updatedImages[shipName] = editUrl.trim();
      }
      
      const docRef = doc(db, 'cruiseMetadata', selectedClData.id);
      await updateDoc(docRef, { shipImages: updatedImages });
      toast.success('Mapping updated');
      setEditingShip(null);
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error('Failed to update mapping');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDefaultImage = async (url: string) => {
    if (!selectedClData || !selectedClData.id) return;
    
    setSaving(true);
    try {
      const updatedImages = {
        ...(selectedClData.shipImages || {})
      };
      
      if (url.trim()) {
        updatedImages["Default"] = url.trim();
      } else {
        delete updatedImages["Default"];
      }
      
      const docRef = doc(db, 'cruiseMetadata', selectedClData.id);
      await updateDoc(docRef, { shipImages: updatedImages });
      toast.success(url.trim() ? 'Default image configured!' : 'Default image removed');
      setEditingDefault(false);
      await refreshCruiseMetadata();
    } catch (error) {
      console.error('Error saving default image:', error);
      toast.error('Failed to configure default image');
    } finally {
      setSaving(false);
    }
  };

  // Filtered and searched ships
  const filteredShips = shipsData.filter(ship => {
    const matchesSearch = ship.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (ship.mappedUrl || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    return ship.status === filter && matchesSearch;
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-6xl mx-auto space-y-6 pb-20"
    >
      <div className="flex items-center gap-4">
        <Link
          to="/admin"
          className="mr-4 p-2 rounded-full hover:bg-gray-200 transition-colors"
          aria-label="Go to Admin"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <ImageIcon className="h-8 w-8 text-emerald-600" />
            Ship Photo Manager
          </h1>
          <p className="text-gray-600 mt-1">Verify existing photos, discover unlinked files, and map ship photos</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          {/* Cruise Line Selector */}
          <div>
            <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Select Cruise Line</label>
            <select
              value={selectedCruiseLine}
              onChange={(e) => setSelectedCruiseLine(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-1.5 bg-white text-sm focus:border-indigo-500 focus:outline-none"
            >
              <option value="">-- Choose Cruise Line --</option>
              {cruiseLines.map(cl => (
                <option key={cl.id} value={cl.name}>{cl.name}</option>
              ))}
            </select>
          </div>

          {/* Search and Rescan wrapper */}
          <div className="md:col-span-2 flex gap-3 items-end w-full">
            {selectedCruiseLine && (
              <>
                <div className="flex-1">
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Search Ship Name</label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search ship name..."
                      className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Reload Button */}
                <button
                  onClick={probeShips}
                  disabled={probing}
                  className="flex items-center gap-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-1.5 disabled:opacity-50 transition-colors h-[34px] shrink-0"
                >
                  <RefreshCw className={`h-4 w-4 ${probing ? 'animate-spin' : ''}`} />
                  Rescan
                </button>
              </>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        {selectedCruiseLine && (
          <div className="flex border-b border-gray-100 gap-6 text-sm pt-2">
            {(['all', 'unmapped', 'broken', 'valid'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`pb-2.5 font-semibold capitalize border-b-2 transition-colors ${
                  filter === tab
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'all' && `All Ships (${shipsData.length})`}
                {tab === 'unmapped' && `Missing Mappings (${shipsData.filter(s => s.status === 'unmapped').length})`}
                {tab === 'broken' && `Broken Images (${shipsData.filter(s => s.status === 'broken').length})`}
                {tab === 'valid' && `Valid Images (${shipsData.filter(s => s.status === 'valid').length})`}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Cruise Line Default Photo Manager */}
      {selectedCruiseLine && !probing && (
        <div className="bg-white rounded-lg border border-gray-200 p-5 shadow-sm space-y-3">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-3">
            <div>
              <h2 className="text-sm font-bold text-gray-850 uppercase tracking-wider flex items-center gap-1.5">
                <ImageIcon className="h-4 w-4 text-emerald-600" />
                Default Cruise Line Photo fallback
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Configured fallback photo when specific ship images are missing.
              </p>
            </div>
            
            {/* Status Indicator */}
            {selectedClData?.shipImages?.["Default"] ? (
              <div className="flex items-center gap-1.5">
                {defaultProbedStatus === 'valid' && (
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Valid Default Image
                  </span>
                )}
                {defaultProbedStatus === 'broken' && (
                  <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Broken Default Link
                  </span>
                )}
              </div>
            ) : (
              <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                <HelpCircle className="h-3 w-3 mr-1" />
                No Default Configured
              </span>
            )}
          </div>

          {editingDefault ? (
            <div className="flex flex-col sm:flex-row gap-3 items-center pt-2">
              <input
                type="text"
                value={defaultUrlInput}
                onChange={(e) => setDefaultUrlInput(e.target.value)}
                placeholder="Enter default image URL (e.g. /images/royal_default.jpg)..."
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none"
              />
              <div className="flex gap-2 w-full sm:w-auto shrink-0 justify-end">
                <button
                  onClick={() => handleSaveDefaultImage(defaultUrlInput)}
                  disabled={saving}
                  className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold transition-colors"
                >
                  <Save className="h-3.5 w-3.5" />
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingDefault(false);
                    setDefaultUrlInput('');
                  }}
                  disabled={saving}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded text-xs font-semibold transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between pt-1">
              <div className="flex items-center gap-3 w-full min-w-0">
                {selectedClData?.shipImages?.["Default"] ? (
                  <>
                    <div className="h-12 w-20 rounded bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center shrink-0">
                      <img
                        src={selectedClData.shipImages["Default"]}
                        alt="Default Cruise Line fallback"
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLElement).style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-mono text-gray-600 truncate">
                        {selectedClData.shipImages["Default"]}
                      </p>
                      <button
                        onClick={() => {
                          setDefaultUrlInput(selectedClData.shipImages?.["Default"] || '');
                          setEditingDefault(true);
                        }}
                        className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold mt-1 inline-flex items-center gap-1"
                      >
                        Edit default URL
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-gray-500 flex-1">
                    No default fallback image has been set for {selectedCruiseLine}. If a ship doesn't have an image, it will fall back to a generic colored gradient.
                    {defaultSuggestion && (
                      <div className="mt-2 p-2 bg-emerald-50/50 border border-emerald-100 rounded flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <span className="text-emerald-800 font-medium">
                          Detected matching asset: <code className="font-mono text-[10px] bg-emerald-100 px-1 rounded">{defaultSuggestion}</code>
                        </span>
                        <button
                          onClick={() => handleSaveDefaultImage(defaultSuggestion)}
                          className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-bold self-end sm:self-center transition-colors shadow-sm animate-pulse"
                        >
                          Use this photo
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {!selectedClData?.shipImages?.["Default"] && !editingDefault && (
                <button
                  onClick={() => {
                    setDefaultUrlInput('');
                    setEditingDefault(true);
                  }}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-semibold transition-colors shadow shrink-0"
                >
                  Configure Default Image
                </button>
              )}

              {selectedClData?.shipImages?.["Default"] && (
                <button
                  onClick={() => handleSaveDefaultImage('')}
                  className="px-3 py-1.5 border border-red-250 hover:bg-red-50 text-red-650 rounded text-xs font-semibold transition-colors shrink-0"
                >
                  Remove Default
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Main Grid View */}
      {probing ? (
        <div className="flex justify-center items-center py-20 bg-white rounded-lg border border-gray-200 shadow-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
            <p className="text-gray-500 font-medium">Probing ship images on server...</p>
          </div>
        </div>
      ) : selectedCruiseLine ? (
        filteredShips.length === 0 ? (
          <div className="p-12 text-center text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col items-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mb-2" />
            <p className="text-lg font-semibold text-gray-800">No ships match the filter criteria</p>
            <p className="text-sm text-gray-500 mt-1">Try changing the filter or clearing the search query.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredShips.map(ship => (
              <div 
                key={ship.name} 
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col justify-between"
              >
                {/* Header Info */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg leading-tight">{ship.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{selectedCruiseLine}</p>
                  </div>
                  
                  {/* Status Badges */}
                  <div>
                    {ship.status === 'valid' && (
                      <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-800">
                        Valid
                      </span>
                    )}
                    {ship.status === 'broken' && (
                      <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">
                        Broken File
                      </span>
                    )}
                    {ship.status === 'unmapped' && (
                      <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800">
                        No Mapping
                      </span>
                    )}
                  </div>
                </div>

                {/* Thumbnail Preview Area */}
                <div className="bg-gray-50 h-44 flex items-center justify-center relative overflow-hidden border-b border-gray-100">
                  {ship.status === 'valid' && ship.mappedUrl ? (
                    <img 
                      src={ship.mappedUrl} 
                      alt={ship.name} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // In case browser cache fails
                        (e.target as HTMLImageElement).src = '';
                      }}
                    />
                  ) : ship.suggestedUrl ? (
                    <div className="w-full h-full relative">
                      <img 
                        src={ship.suggestedUrl} 
                        alt="Suggestion Preview" 
                        className="w-full h-full object-cover opacity-40 blur-xs"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 to-transparent flex flex-col justify-end p-4 text-white">
                        <span className="text-xs bg-emerald-600 px-2 py-0.5 rounded font-bold self-start mb-1">Detected Match</span>
                        <p className="text-xs font-semibold font-mono truncate">{ship.suggestedUrl}</p>
                        <button
                          onClick={() => handleQuickMap(ship.name, ship.suggestedUrl!)}
                          disabled={saving}
                          className="mt-2 text-xs bg-white text-gray-900 font-bold py-1.5 px-3 rounded shadow hover:bg-gray-100 transition-colors self-stretch text-center"
                        >
                          Quick Link to Match
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center text-gray-400 p-4 text-center">
                      <ImageIcon className="h-10 w-10 mb-1 opacity-60" />
                      <p className="text-xs font-medium">No Image File Detected</p>
                      <p className="text-[10px] opacity-75 mt-0.5">Upload to github: <code>public/images/</code></p>
                    </div>
                  )}
                </div>

                {/* Edit Form */}
                <div className="p-4 bg-gray-50/50 flex-1 flex flex-col justify-end">
                  {editingShip === ship.name ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Image URL Path</label>
                        <input
                          type="text"
                          value={editUrl}
                          onChange={(e) => setEditUrl(e.target.value)}
                          placeholder="/images/file_name.jpg"
                          className="w-full rounded border border-gray-300 px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 font-mono"
                        />
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSaveEdit(ship.name)}
                          disabled={saving}
                          className="flex-1 flex justify-center items-center gap-1 rounded bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs py-1.5 transition-colors"
                        >
                          <Save className="h-3 w-3" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingShip(null)}
                          className="flex-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-1.5 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="text-xs">
                        <span className="font-semibold text-gray-600">Mapped URL: </span>
                        {ship.mappedUrl ? (
                          <span className="font-mono text-gray-800 break-all">{ship.mappedUrl}</span>
                        ) : (
                          <span className="text-amber-600 italic">Unmapped</span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStartEdit(ship)}
                          className="flex-1 rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-xs py-1.5 transition-colors"
                        >
                          Edit Mapping
                        </button>
                        {ship.mappedUrl && (
                          <a
                            href={ship.mappedUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="rounded border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 px-2 py-1.5 transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-accent"
                            aria-label="View mapped photo"
                          >
                            <Eye className="h-4.5 w-4.5" />
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        <div className="p-16 text-center text-gray-500 bg-white rounded-lg border border-gray-200 shadow-sm flex flex-col items-center">
          <HelpCircle className="h-16 w-16 text-gray-400 mb-2 opacity-60" />
          <p className="text-lg font-semibold text-gray-800">No Cruise Line Selected</p>
          <p className="text-sm text-gray-500 mt-1">Please select a cruise line from the dropdown above to scan its ships.</p>
        </div>
      )}
    </motion.div>
  );
};

export default AdminPhotoManager;
